import { useState, useEffect, useCallback } from 'react'
import { Group, Text, Button, Loader, Box } from '@mantine/core'
import { IconFolder, IconPin } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { ResizableDrawer } from '@/components/ui'
import { api } from '@/api/client'
import { FileTree } from './FileTree'
import { FileList } from './FileList'
import { FilePreview } from './FilePreview'
import type { FileItem, FileListResponse } from './types'
import { ALLOWED_ROOTS } from './types'

// ---------------------------------------------------------------------------
// Exported types (used by Recursos.tsx — Task 11)
// ---------------------------------------------------------------------------

export interface FileBrowserTarget {
  instanceId: string
  instanceName: string
}

export interface PinnedFileBrowser {
  id: string
  instanceId: string
  instanceName: string
  tenantId: number
  currentPath: string
  position: { x: number; y: number }
  size: { w: number; h: number }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface FileBrowserDrawerProps {
  target: FileBrowserTarget | null
  tenantId: number | null
  onClose: () => void
  onPin: (win: Omit<PinnedFileBrowser, 'id'>) => void
}

export function FileBrowserDrawer({
  target,
  tenantId,
  onClose,
  onPin,
}: FileBrowserDrawerProps) {
  const [currentPath, setCurrentPath] = useState<string>(ALLOWED_ROOTS[0])
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null)
  const [previewExpanded, setPreviewExpanded] = useState(false)

  // Reset state when switching instances
  useEffect(() => {
    if (target) {
      setCurrentPath(ALLOWED_ROOTS[0])
      setPreviewFile(null)
    }
  }, [target?.instanceId])

  const { data, isLoading } = useQuery<FileListResponse>({
    queryKey: ['ec2-files', tenantId, target?.instanceId, currentPath],
    queryFn: async () => {
      const res = await api.get<FileListResponse>(
        `/v1/tenants/${tenantId}/inventory/ec2/${target!.instanceId}/files`,
        { params: { path: currentPath } },
      )
      return res.data
    },
    enabled: !!target && !!tenantId,
    retry: false,
  })

  const [blockedNames, setBlockedNames] = useState<Set<string>>(new Set())

  // Reset blocked names when switching directories
  useEffect(() => {
    setBlockedNames(new Set())
  }, [currentPath])

  const handleBlocked = useCallback((fileName: string) => {
    setBlockedNames((prev) => new Set(prev).add(fileName))
  }, [])

  const items = (data?.items ?? []).map((item) =>
    blockedNames.has(item.name) ? { ...item, blocked: true } : item,
  )

  const handleNavigate = useCallback((path: string) => {
    setCurrentPath(path)
    setPreviewFile(null)
  }, [])

  const handlePin = useCallback(() => {
    if (!target || !tenantId) return
    onPin({
      instanceId: target.instanceId,
      instanceName: target.instanceName,
      tenantId,
      currentPath,
      position: { x: Math.max(40, window.innerWidth - 760), y: 60 },
      size: { w: 700, h: 500 },
    })
    onClose()
  }, [target, tenantId, currentPath, onPin, onClose])

  return (
    <ResizableDrawer
      storageKey="file-browser-drawer-width"
      defaultWidth={850}
      minWidth={600}
      opened={!!target}
      position="right"
      onClose={onClose}
      title={
        <Group gap="xs">
          <IconFolder size={18} />
          <Text fw={500}>Arquivos — {target?.instanceName}</Text>
          {isLoading && <Loader size={12} />}
        </Group>
      }
    >
      <Box style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 60px)' }}>
        {/* Pin button row */}
        <Group
          justify="flex-end"
          px="sm"
          py={6}
          style={{ borderBottom: '1px solid var(--mantine-color-dark-4)', flexShrink: 0 }}
        >
          <Button
            variant="subtle"
            size="compact-sm"
            leftSection={<IconPin size={14} />}
            onClick={handlePin}
          >
            Pinar
          </Button>
        </Group>

        {/* Main content: tree + list */}
        <Box style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
          {/* Left panel — FileTree */}
          <Box style={{ width: 220, flexShrink: 0, borderRight: '1px solid var(--mantine-color-dark-4)' }}>
            {target && tenantId && (
              <FileTree
                tenantId={tenantId}
                instanceId={target.instanceId}
                onSelect={handleNavigate}
              />
            )}
          </Box>

          {/* Right panel — FileList + FilePreview */}
          <Box style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
            {!previewExpanded && (
              <Box style={{ flex: 1, overflow: 'hidden' }}>
                <FileList
                  items={items}
                  currentPath={currentPath}
                  onNavigate={handleNavigate}
                  onPreview={setPreviewFile}
                />
              </Box>
            )}

            {previewFile && target && tenantId && (
              <FilePreview
                tenantId={tenantId}
                instanceId={target.instanceId}
                file={previewFile}
                filePath={`${currentPath}/${previewFile.name}`}
                onClose={() => { setPreviewFile(null); setPreviewExpanded(false) }}
                onBlocked={handleBlocked}
                expanded={previewExpanded}
                onToggleExpand={() => setPreviewExpanded((v) => !v)}
              />
            )}
          </Box>
        </Box>
      </Box>
    </ResizableDrawer>
  )
}
